import { useEffect, useId, useState, type ReactNode } from 'react';
import { GRADE_LABELS, gradeChange, type ApprovalEventDto, type Grade } from '@bjj/shared';
import { ApiError } from '../api/client';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="btn-row">{actions}</div> : null}
    </div>
  );
}

export function Card({
  title,
  children,
  actions,
}: {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="card" aria-label={title}>
      {title || actions ? (
        <div className="page-header" style={{ marginBottom: 12 }}>
          {title ? <h2>{title}</h2> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

type FieldProps = {
  label: string;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
};

export function Field({ label, hint, children }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : 'Something went wrong';
  const details = error instanceof ApiError ? error.details : undefined;
  const missing = (details as { missing?: Array<{ name: string }> } | undefined)?.missing;
  const cycle = (details as { cycle?: Array<{ name: string }> } | undefined)?.cycle;
  const fieldErrors = Array.isArray(details)
    ? (details as Array<{ path: string; message: string }>)
    : null;
  return (
    <div className="alert error" role="alert">
      {message}
      {missing?.length ? <div>Missing: {missing.map((m) => m.name).join(', ')}</div> : null}
      {cycle?.length ? <div>Cycle: {cycle.map((m) => m.name).join(' → ')}</div> : null}
      {fieldErrors?.length ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {fieldErrors.map((f) => (
            <li key={f.path + f.message}>
              {f.path ? `${f.path}: ` : ''}
              {f.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  ready: 'green',
  accepted: 'green',
  approved: 'green',
  mastered: 'green',
  pending: 'blue',
  processing: 'blue',
  pending_upload: 'blue',
  available: 'blue',
  suspended: 'amber',
  expired: 'amber',
  locked: 'amber',
  hidden: 'amber',
  revoked: 'red',
  rescinded: 'red',
  error: 'red',
  inactive: 'red',
  override: 'amber',
};

export function Badge({ value, label }: { value: string; label?: string }) {
  return (
    <span className={`badge ${STATUS_COLORS[value] ?? ''}`}>
      {label ?? value.replace('_', ' ')}
    </span>
  );
}

/** A–C grade as a solid letter chip; only A counts as mastered. */
export function GradeBadge({ grade, showLabel = false }: { grade: Grade; showLabel?: boolean }) {
  return (
    <span className="grade-badge" aria-label={`Grade ${grade}, ${GRADE_LABELS[grade]}`}>
      <span className={`grade grade-${grade}`} aria-hidden="true">
        {grade}
      </span>
      {showLabel ? <span className="grade-label">{GRADE_LABELS[grade]}</span> : null}
    </span>
  );
}

/** "Graded B", "Raised to A (was B)", "Lowered to B (was A)" or "Grade removed". */
export function gradeEventText(e: Pick<ApprovalEventDto, 'type' | 'grade' | 'previousGrade'>) {
  if (e.type === 'rescinded' || !e.grade) return `Grade ${e.previousGrade ?? ''} removed`.trim();
  const change = gradeChange(e.previousGrade, e.grade);
  return change === 'raised'
    ? `Raised to ${e.grade} (was ${e.previousGrade})`
    : change === 'lowered'
      ? `Lowered to ${e.grade} (was ${e.previousGrade})`
      : `Graded ${e.grade}`;
}

export function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        className="progress"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Completion'}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <span className="muted">{percent}%</span>
    </div>
  );
}

/**
 * Two-step destructive button: the first click arms it, the second performs the action. Disarms
 * itself after a few seconds. Avoids blocking browser confirm() dialogs.
 */
export function ConfirmButton({
  children,
  confirmLabel = 'Click again to confirm',
  onConfirm,
  disabled,
  className = 'btn danger small',
}: {
  children: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      className={`${className} ${armed ? 'confirming' : ''}`}
      disabled={disabled}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
        }
      }}
    >
      {armed ? confirmLabel : children}
    </button>
  );
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** When a grade was given: offline scans show the time they were scanned, not when they synced. */
export function gradedWhen(e: Pick<ApprovalEventDto, 'createdAt' | 'scannedAt'>): string {
  return e.scannedAt ? `${formatDate(e.scannedAt)} · offline scan` : formatDate(e.createdAt);
}

export function Loading() {
  return <div className="empty">Loading…</div>;
}
